import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { useBulkLabelGeneration, useBulkCategoryLabelGeneration, useBulkShowLabelGeneration, useBulkCollectionLabelGeneration } from '../hooks/bulk-label-generation'
import { useNotificationBar } from './notificationContext'

type EntityType = 'episode' | 'category' | 'show' | 'collection'

interface BulkGenerationJob {
  id: string
  entityType: EntityType
  entityIds: string[]
  totalEntities: number
  status: 'running' | 'completed' | 'failed'
  startedAt: Date
  completedAt?: Date
  result?: any
  error?: string
}

interface BulkLabelGenerationContextType {
  currentJob: BulkGenerationJob | null
  startGeneration: (entityIds: string[], prompt: string, entityType?: EntityType) => void
  clearJob: () => void
  isGenerating: boolean
}

const BulkLabelGenerationContext = createContext<BulkLabelGenerationContextType | undefined>(undefined)

export const BulkLabelGenerationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentJob, setCurrentJob] = useState<BulkGenerationJob | null>(null)
  const bulkLabelGeneration = useBulkLabelGeneration()
  const bulkCategoryLabelGeneration = useBulkCategoryLabelGeneration()
  const bulkShowLabelGeneration = useBulkShowLabelGeneration()
  const bulkCollectionLabelGeneration = useBulkCollectionLabelGeneration()
  const notification = useNotificationBar()

  const startGeneration = useCallback(async (entityIds: string[], prompt: string, entityType: EntityType = 'episode') => {
    const jobId = `job_${Date.now()}`
    
    // Create job
    const job: BulkGenerationJob = {
      id: jobId,
      entityType,
      entityIds,
      totalEntities: entityIds.length,
      status: 'running',
      startedAt: new Date()
    }
    
    setCurrentJob(job)
    
    // Entity name mapping
    const entityNames: Record<EntityType, string> = {
      episode: 'episode',
      category: 'category',
      show: 'show',
      collection: 'collection'
    }
    
    const entityName = entityNames[entityType]
    const pluralSuffix = entityIds.length > 1 ? 's' : ''
    
    // Show notification
    notification.push({
      title: 'Label Generation Started',
      type: 'info',
      message: `Processing ${entityIds.length} ${entityName}${pluralSuffix} in the background. You can continue working.`
    })

    // Start generation in background
    try {
      let result: any
      
      switch (entityType) {
        case 'episode':
          result = await bulkLabelGeneration.mutateAsync({
            episode_ids: entityIds,
            prompt: prompt
          })
          break
        case 'category':
          result = await bulkCategoryLabelGeneration.mutateAsync({
            category_ids: entityIds,
            prompt: prompt
          })
          break
        case 'show':
          result = await bulkShowLabelGeneration.mutateAsync({
            show_ids: entityIds,
            prompt: prompt
          })
          break
        case 'collection':
          result = await bulkCollectionLabelGeneration.mutateAsync({
            collection_ids: entityIds,
            prompt: prompt
          })
          break
      }

      // Update job as completed
      setCurrentJob(prev => prev ? {
        ...prev,
        status: 'completed',
        completedAt: new Date(),
        result: result.data
      } : null)

      // Show success notification
      // Try multiple possible field name patterns from the API response
      const successCount = result.data?.successful_episodes || 
                          result.data?.successful_categories || 
                          result.data?.successful_shows || 
                          result.data?.successful_collections ||
                          result.data?.[`successful_${entityType}s`] ||
                          0
      
      const totalCount = result.data?.total_episodes || 
                        result.data?.total_categories || 
                        result.data?.total_shows || 
                        result.data?.total_collections ||
                        result.data?.[`total_${entityType}s`] ||
                        entityIds.length
      
      notification.push({
        title: 'Label Generation Complete',
        type: 'success',
        message: `Successfully generated labels for ${successCount}/${totalCount} ${entityName}${pluralSuffix}`
      })

    } catch (error: any) {
      // Update job as failed
      setCurrentJob(prev => prev ? {
        ...prev,
        status: 'failed',
        completedAt: new Date(),
        error: error?.response?.data?.message || 'Failed to generate labels'
      } : null)

      // Show error notification
      notification.push({
        title: 'Label Generation Failed',
        type: 'error',
        message: error?.response?.data?.message || 'Failed to generate labels. Please try again.'
      })
    }
  }, [bulkLabelGeneration, bulkCategoryLabelGeneration, bulkShowLabelGeneration, bulkCollectionLabelGeneration, notification])

  const clearJob = useCallback(() => {
    setCurrentJob(null)
  }, [])

  const value = {
    currentJob,
    startGeneration,
    clearJob,
    isGenerating: currentJob?.status === 'running'
  }

  return (
    <BulkLabelGenerationContext.Provider value={value}>
      {children}
    </BulkLabelGenerationContext.Provider>
  )
}

export const useBulkLabelGenerationContext = () => {
  const context = useContext(BulkLabelGenerationContext)
  if (!context) {
    throw new Error('useBulkLabelGenerationContext must be used within BulkLabelGenerationProvider')
  }
  return context
}
